// Full-session mixed audio recorder for the AI Interview.
//
// The admin's Student Report has an "Audio" button that already works for
// other assessment types (Communication/Paragraph Reading) — it reads a
// student_answers.object_key uploaded earlier. AI Interview never produced
// that file, so the button showed nothing. This module records the ENTIRE
// interview — the AI interviewer's spoken questions AND the candidate's
// spoken answers, mixed into one continuous track — so that button works for
// AI Interview too via the same upload endpoint.
//
// How the mix works (Web Audio API):
//   candidate mic  ─┐
//                    ├─▶ MediaStreamAudioDestinationNode ─▶ MediaRecorder
//   AI TTS <audio> ─┘        (this IS the recorded stream)
//
// The TTS element is tapped via createMediaElementSource, which redirects the
// element's output through the audio graph — so it must ALSO be explicitly
// reconnected to context.destination or the interviewer's voice would go
// silent for the candidate (createMediaElementSource does not fan out to
// speakers by default once connected elsewhere).
//
// createMediaElementSource may be called only ONCE per <audio> element. This
// only works because interview.js reuses a single module-level TTS element
// (ttsAudio.js) for the whole session — never a fresh `new Audio()`.

let ctx = null
let destNode = null
let micSourceNode = null
let ttsSourceNode = null
let recorder = null
let chunks = []
let mimeType = ''
let startedAt = 0

const isSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  typeof window.AudioContext !== 'undefined'

// Idempotent: safe to call multiple times (e.g. once the mic stream is ready,
// separately once the TTS element exists) — only wires up nodes that aren't
// already connected.
export const startSessionRecording = ({ micStream, ttsAudioEl, mimeCandidate }) => {
  if (!isSupported()) return false
  try {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext
      ctx = new AC()
    }
    if (ctx.state === 'suspended') {
      // Best-effort; a user gesture already unlocked the TTS element earlier
      // in the flow (primeTtsAudio), so this usually resumes immediately.
      ctx.resume().catch(() => {})
    }
    if (!destNode) destNode = ctx.createMediaStreamDestination()

    if (micStream && !micSourceNode) {
      micSourceNode = ctx.createMediaStreamSource(micStream)
      micSourceNode.connect(destNode)
    }
    if (ttsAudioEl && !ttsSourceNode) {
      ttsSourceNode = ctx.createMediaElementSource(ttsAudioEl)
      ttsSourceNode.connect(destNode)
      // Also route to real speakers — createMediaElementSource intercepts the
      // element's normal output, so without this the candidate stops hearing
      // the interviewer once we tap the element.
      ttsSourceNode.connect(ctx.destination)
    }

    if (!recorder && destNode.stream.getAudioTracks().length > 0) {
      mimeType = mimeCandidate || ''
      const rec = mimeType
        ? new window.MediaRecorder(destNode.stream, { mimeType })
        : new window.MediaRecorder(destNode.stream)
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunks.push(ev.data)
      }
      rec.start(1000)
      recorder = rec
      startedAt = Date.now()
    }
    return true
  } catch (e) {
    console.warn('[session-recorder] start failed (non-fatal — interview continues):', e?.message || e)
    return false
  }
}

// Call once the mic stream is acquired to (re)wire the mic branch if the
// recorder was already started with just the TTS branch, or vice versa.
export const attachMicToSessionRecording = (micStream) => {
  if (!ctx || !destNode || micSourceNode || !micStream) return
  try {
    micSourceNode = ctx.createMediaStreamSource(micStream)
    micSourceNode.connect(destNode)
  } catch (e) {
    console.warn('[session-recorder] mic attach failed:', e?.message || e)
  }
}

export const attachTtsToSessionRecording = (ttsAudioEl) => {
  if (!ctx || !destNode || ttsSourceNode || !ttsAudioEl) return
  try {
    ttsSourceNode = ctx.createMediaElementSource(ttsAudioEl)
    ttsSourceNode.connect(destNode)
    ttsSourceNode.connect(ctx.destination)
  } catch (e) {
    console.warn('[session-recorder] tts attach failed:', e?.message || e)
  }
}

export const isSessionRecordingActive = () => !!recorder && recorder.state === 'recording'

// Elapsed ms since the recording actually started — used to timestamp each
// question's position within the mixed track (for admin-side chapter jumps).
// null if recording never started.
export const getRecordingElapsedMs = () => (startedAt ? Date.now() - startedAt : null)

// Cumulative snapshot of everything recorded so far, WITHOUT stopping the
// recorder — the interview keeps recording into the same chunks[] after this
// returns. Used by the periodic flush (interview.js) so a candidate who closes
// the tab / loses power mid-interview still has their audio server-side: the
// stored object is only ever one flush interval behind, instead of being lost
// outright because finalize() never ran.
//
// Why concatenating a prefix of chunks[] is valid: MediaRecorder puts the
// container header in the FIRST chunk only, so chunks[0..n] is a well-formed
// streamable WebM missing just the trailing duration/cues that stop() would
// have written. ffmpeg (the backend's transcodeToMp3) decodes that fine. The
// individual chunks are NOT independently playable, which is exactly why this
// re-sends the cumulative blob rather than shipping each 1s chunk separately.
//
// Returns null if nothing is buffered yet (recording never started, or already
// torn down). Never throws.
export const snapshotSessionRecording = () => {
  try {
    if (!chunks.length) return null
    return {
      blob: new window.Blob(chunks, { type: mimeType || 'audio/webm' }),
      mimeType: mimeType || 'audio/webm',
      durationMs: startedAt ? Date.now() - startedAt : 0,
    }
  } catch (e) {
    console.warn('[session-recorder] snapshot failed (non-fatal):', e?.message || e)
    return null
  }
}

// Stops the recorder and resolves with { blob, mimeType, durationMs } — or
// null if nothing was recording (never started, or start() failed silently).
// Never throws; a failed recording must not block interview submission.
//
// Robustness matters here: the resolution used to depend SOLELY on the
// MediaRecorder firing `onstop`. When finalize() runs at "time's up" — i.e.
// while the candidate is still mid-answer with the mic actively recording and
// the audio graph under load — some browsers are slow to, or never, fire
// `onstop`. That hung this promise forever, so the .then() that persists +
// uploads the recording never ran and the audio was silently lost, even though
// the interview otherwise completed. Two guards below prevent that:
//   1. requestData() force-flushes any buffered audio as a dataavailable
//      BEFORE stop(), so we still get the tail even if the final chunk on
//      stop() is dropped.
//   2. a timeout resolves with whatever chunks we have if onstop is late/never.
// We also salvage buffered chunks even when the recorder is already inactive.
export const stopSessionRecording = () =>
  new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      const blob = chunks.length ? new window.Blob(chunks, { type: mimeType || 'audio/webm' }) : null
      const durationMs = startedAt ? Date.now() - startedAt : 0
      resolve(blob ? { blob, mimeType: mimeType || blob.type, durationMs } : null)
    }
    if (!recorder || recorder.state === 'inactive') {
      // Already stopped elsewhere — still return any buffered audio.
      finish()
      return
    }
    try {
      recorder.onstop = finish
      try { recorder.requestData() } catch (_) {}
      recorder.stop()
    } catch (_) {
      finish()
    }
    // Safety net against a slow/absent onstop (heavy audio graph at time-up).
    setTimeout(finish, 3000)
  })

// Tears down the audio graph. Call after stopSessionRecording() resolves (or
// on unmount) so a re-mounted interview (rare, but possible on error-boundary
// recovery) starts clean.
export const teardownSessionRecording = () => {
  try { micSourceNode?.disconnect() } catch (_) {}
  try { ttsSourceNode?.disconnect() } catch (_) {}
  try { destNode?.disconnect() } catch (_) {}
  try { if (ctx && ctx.state !== 'closed') ctx.close() } catch (_) {}
  ctx = null
  destNode = null
  micSourceNode = null
  ttsSourceNode = null
  recorder = null
  chunks = []
  mimeType = ''
  startedAt = 0
}
